import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash.startcase';
import Button from '../Button/Button';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';
import useGtmHook from '../../util/hooks/useGtmHook';
import { trackTriggerOrderPlaced } from './Common/gtmEventHelper';
import {
  ModalCloseIcon,
  ConfirmationContainer,
  ConfirmationCloseButton,
  ConfirmationFooterWrapper,
  ConfirmationProductImage,
  ConfirmationTitle,
  ConfirmationProductLine,
  ConfirmationSubText,
  StoringInVaultText,
  ReviewVaultRow,
  SegregatedTag,
  SavingsBanner,
  SavingsBannerIcon,
  ConfirmationNotifyText,
  ConfirmationButtonContainer,
} from './Common';

// Final wizard step: static "order placed" success screen. The order itself
// is created by TargetPriceOrderReview's POST trigger-orders/quotes (executed
// against the quote fetched on Review entry), but there's no trigger-orders
// list page yet to route "View Order" to, so this still just closes the
// modal. Modeled on PriceAlerts' AlertConfirmation (its own close button +
// setWizardTitle('') to collapse BasicModal's header), reusing the quote's
// BE-sourced savings figure — same source Review displays — rather than
// recomputing it from the live price.
const TargetPriceOrderConfirmation = ({
  wizardData,
  closeModal,
  setWizardTitle,
  setGoBackHandler,
  stepIndex,
  currentStepIndex,
  isGS,
}) => {
  const { t } = useTranslation();
  const { ecommerceGtmEvent } = useGtmHook();
  const { RootUrl, SegregatedCaption, InstavaultProductCodes } = useSelector(
    selectSettings,
  );

  const product = wizardData?.selectedProduct;
  const vault = wizardData?.selectedVault;
  const quantity = Number(wizardData?.orderValues?.QuantityAmount) || 0;
  const targetPrice = Number(wizardData?.orderValues?.CashAmount) || 0;
  const isSegregatedStorage = wizardData?.selectedPlan === 'premium';
  // The quote fetched by TargetPriceOrderEntry's GET trigger-orders/quotes
  // call, executed as-is by Review's submit — same BE-sourced figure Review
  // displays, not recomputed from the live price.
  const savings = Number(wizardData?.quote?.TargetPriceSavings) || 0;

  const vaultName = vault
    ? startCase(parseEnumType(productLocationTypes, vault.LocationType))
    : '';

  // Mirrors TargetPriceOrderReview's isInstaVaultProduct check — instavault
  // products store in one implicit location, so hide the "Storing in
  // {vault} Vault" line entirely.
  const productCode = product?.ProductCode || product?.Code;
  const isInstaVaultProduct =
    isGS &&
    (productCode === InstavaultProductCodes?.[0] ||
      productCode === InstavaultProductCodes?.[1]);

  const imageSource = product
    ? config.algoliaSearch.imageCode
        .replace('{product_code}', product.ProductCode || product.Code)
        .replace('{base_url}', RootUrl)
    : '';

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
      setWizardTitle('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  useEffect(() => {
    trackTriggerOrderPlaced({
      ecommerceGtmEvent,
      productCode: product?.ProductCode || product?.Code,
      targetPrice,
      quantity,
      vault: vaultName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfirmationContainer>
      <ConfirmationCloseButton
        onClick={closeModal}
        data-cy="button-target-price-confirmation-close-icon"
      >
        <ModalCloseIcon />
      </ConfirmationCloseButton>

      <ConfirmationProductImage
        src={encodeURI(imageSource)}
        alt={product?.Caption}
      />

      <ConfirmationTitle>
        {t('gtt.triggerOrders.confirmation.title')}
      </ConfirmationTitle>
      <ConfirmationProductLine>
        {quantity} × {product?.ShortCaption}
      </ConfirmationProductLine>
      <ConfirmationSubText>
        {t('gtt.triggerOrders.confirmation.executesAt', {
          price: formatMoneyNumeral(targetPrice),
        })}
      </ConfirmationSubText>
      {!isInstaVaultProduct && (
        <ReviewVaultRow>
          <StoringInVaultText
            translationKey="gtt.triggerOrders.confirmation.storingIn"
            vaultName={vaultName}
          />
          {isSegregatedStorage && (
            <SegregatedTag>
              {SegregatedCaption || t('buyWizard.premium')}
            </SegregatedTag>
          )}
        </ReviewVaultRow>
      )}

      <ConfirmationFooterWrapper>
        <SavingsBanner>
          <SavingsBannerIcon />
          {t('gtt.triggerOrders.confirmation.savingsText')}
          <b>{formatMoneyNumeral(savings)}</b>
        </SavingsBanner>
        <ConfirmationNotifyText>
          {t('gtt.triggerOrders.confirmation.notifyText')}
        </ConfirmationNotifyText>

        <ConfirmationButtonContainer>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            style={{ width: '100%' }}
            // Placeholder: the order is now created server-side (Review's
            // POST trigger-orders/quotes), but there's no trigger-orders list
            // page yet to route to, so this still just closes the modal. Wire
            // to a real destination once that page exists.
            onClick={closeModal}
            data-cy="button-target-price-confirmation-view-order"
          >
            {t('gtt.triggerOrders.confirmation.viewOrder')}
          </Button>
        </ConfirmationButtonContainer>
      </ConfirmationFooterWrapper>
    </ConfirmationContainer>
  );
};

TargetPriceOrderConfirmation.propTypes = {
  wizardData: PropTypes.shape({
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
      Caption: PropTypes.string,
      WeightCaption: PropTypes.string,
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    quote: PropTypes.shape({
      TargetPriceSavings: PropTypes.number,
    }),
    selectedPlan: PropTypes.string,
  }),
  closeModal: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isGS: PropTypes.bool,
};

export default TargetPriceOrderConfirmation;
